import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, Circle, Target, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IndicatorProgressPanel
 *
 * Muestra los indicadores curriculares oficiales de la unidad en curso,
 * con visualización clara de cuáles ya completó el alumno (verde) y
 * cuáles le faltan (gris).
 *
 * Visible en todas las materias siempre que haya `grade`, `subject` y
 * `unit` definidos. Se actualiza cada vez que cambia `refreshKey` (el
 * llamador suele incrementarlo después de cada respuesta del tutor).
 *
 * Reglas pedagógicas:
 *  - El alumno NO puede cambiar de unidad hasta tener todos los indicadores.
 *  - El backend lo bloquea en el prompt; este panel lo hace evidente al UI.
 */
export default function IndicatorProgressPanel({ grade, subject, unit, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    if (!grade || !subject || !unit) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${API}/curriculum/${grade}/${subject}/indicators-progress`,
        { params: { unit }, withCredentials: true }
      );
      setData(res.data);
    } catch (e) {
      console.warn("IndicatorProgressPanel: fetch falló", e);
      setError("No se pudo cargar el progreso");
    } finally {
      setLoading(false);
    }
  }, [grade, subject, unit]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress, refreshKey]);

  if (!grade || !subject || !unit) return null;

  if (loading && !data) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="indicator-progress-loading">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Cargando indicadores…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-4" data-testid="indicator-progress-error">
        <p className="text-xs text-red-600">{error}</p>
      </div>
    );
  }

  if (!data || data.total === 0) return null;

  const percent = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 p-4"
      data-testid="indicator-progress-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-emerald-600" />
        <p className="text-xs font-semibold text-gray-700">Indicadores de la unidad</p>
      </div>

      {/* Resumen numérico + barra */}
      <div className="mb-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xs text-gray-500">
            <span
              className="font-semibold text-gray-900"
              data-testid="indicator-progress-count"
            >
              {data.completed}
            </span>
            <span className="text-gray-400"> / {data.total}</span>
            <span className="text-gray-400"> completados</span>
          </span>
          <span
            className={`text-xs font-semibold ${
              data.unit_complete ? "text-emerald-600" : "text-gray-500"
            }`}
            data-testid="indicator-progress-percent"
          >
            {percent}%
          </span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              data.unit_complete
                ? "bg-emerald-500"
                : "bg-gradient-to-r from-emerald-400 to-emerald-500"
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Chips de indicadores */}
      <div className="flex flex-wrap gap-1.5" data-testid="indicator-chips">
        {data.indicators.map((ind) => {
          const isDone = ind.completed;
          return (
            <span
              key={ind.code}
              data-testid={`indicator-chip-${ind.code}${isDone ? "-done" : "-pending"}`}
              title={ind.bloom_level ? `${ind.code} · ${ind.bloom_level}` : ind.code}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                isDone
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-gray-50 text-gray-500 border border-gray-200"
              }`}
            >
              {isDone ? (
                <CheckCircle2 className="w-3 h-3" />
              ) : (
                <Circle className="w-3 h-3" />
              )}
              {ind.code}
            </span>
          );
        })}
      </div>

      {/* Footer pedagógico */}
      <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
        {data.unit_complete ? (
          <span className="text-emerald-700 font-medium">
            ¡Felicidades! Completaste todos los indicadores de la unidad.
            Ahora sí puedes pasar a la siguiente.
          </span>
        ) : (
          <>
            Te faltan <span className="font-semibold text-gray-700">{data.pending}</span>{" "}
            indicador{data.pending === 1 ? "" : "es"} para terminar esta unidad.
            Hasta entonces seguimos aquí.
          </>
        )}
      </p>
    </div>
  );
}
